import * as React from "react";
import { Controller, Control, FieldError } from "react-hook-form";
import { LanguageSpokenTerm, ApiSchemaLanguageData } from "../../models/api/language";
import { useFunctionalityLanguage } from "../../pages/hooks";
import { PlatformType } from "../../models";

const newTermObj = (term: string, languageId?: string): LanguageSpokenTerm => ({
  id:
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  term,
  languageId: languageId ?? "",
});

type LanguageTermsInputProps = {
  control: Control<ApiSchemaLanguageData>;
  name?: "languageSpokenTerms";
  languageId?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: FieldError | undefined;
  minTermLen?: number;
  initialExistingTerms?: LanguageSpokenTerm[];
  onExistingDeleted?: (id: string) => void;
};

export const LanguageTermsInput: React.FC<LanguageTermsInputProps> = ({
  control,
  name = "languageSpokenTerms",
  languageId,
  label = "Spoken Terms",
  placeholder = "Type a term and press Enter…",
  required = false,
  error,
  minTermLen = 2,
  initialExistingTerms = [],
  onExistingDeleted,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const { onDeleteLanguageTerm } = useFunctionalityLanguage({ plaformType: PlatformType.All });

  const [existing, setExisting] = React.useState<LanguageSpokenTerm[]>(initialExistingTerms);
  const [deletingIds, setDeletingIds] = React.useState<Set<string>>(new Set());



  React.useEffect(() => {
    setExisting(initialExistingTerms ?? []);
  }, [initialExistingTerms]);

  const tokenize = (raw: string): string[] =>
    raw.split(/[,;\n\r\t]/g).map((s) => s.trim()).filter(Boolean);

  const tryAddTerms = (
    raw: string,
    currentNew: LanguageSpokenTerm[],
    onChange: (v: LanguageSpokenTerm[]) => void,
    existingKeySet: Set<string>
  ) => {
    const tokens = tokenize(raw);
    if (!tokens.length) return;

    const dedup = new Set<string>([
      ...existingKeySet,
      ...currentNew.map((t) => (t.term ?? "").toLowerCase()),
    ]);

    const toAdd: LanguageSpokenTerm[] = [];
    for (const t of tokens) {
      const norm = t.trim();
      if (norm.length < minTermLen) continue;
      if (dedup.has(norm.toLowerCase())) continue;
      dedup.add(norm.toLowerCase());
      toAdd.push(newTermObj(norm, languageId));
    }

    if (toAdd.length) onChange([...currentNew, ...toAdd]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeExisting = async (id: string) => {
    const safeId = String(id ?? '').trim();
    if (!safeId || safeId.startsWith('tmp_')) {
      return;
    }

    setDeletingIds(prev => new Set(prev).add(safeId));

    try {
      await onDeleteLanguageTerm(safeId);
      onExistingDeleted?.(safeId);
      setExisting(prev => prev.filter(t => t.id !== safeId));
    } catch (e) {
      console.error("Failed to delete language term", e);
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(safeId);
        return next;
      });
    }
  };



  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? "required" : false,
        validate: (arr) =>
          !required || (Array.isArray(arr) && arr.length > 0) || "required",
      }}
      defaultValue={[] as LanguageSpokenTerm[]}
      render={({ field }) => {
        const { value: newTerms = [], onChange } = field;

        const existingKeySet = new Set(
          existing.map((t) => (t.term ?? "").trim().toLowerCase())
        );

        const filteredNewTerms = Array.isArray(newTerms)
          ? newTerms.filter(
            (t) => !existingKeySet.has((t.term ?? "").trim().toLowerCase())
          )
          : [];

        const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
          const target = e.target as HTMLInputElement;
          if (!target) return;
          const confirmKeys = ["Enter", "Tab"];
          const isComma = e.key === ",";
          if (confirmKeys.includes(e.key) || isComma) {
            e.preventDefault();
            tryAddTerms(target.value, filteredNewTerms, onChange, existingKeySet);
          }
        };

        const onBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
          tryAddTerms(e.currentTarget.value, filteredNewTerms, onChange, existingKeySet);
        };

        const onPaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
          const pasted = e.clipboardData.getData("text");
          if (!pasted) return;
          e.preventDefault();
          tryAddTerms(pasted, filteredNewTerms, onChange, existingKeySet);
        };

        return (
          <div className="form-group">
            <label className={required ? "position-relative required" : ""}>
              {label}
            </label>

            {existing.length > 0 && (
              <div className="mb-2 d-flex flex-wrap gap-2">
                {existing.map((t) => {
                  const isDeleting = deletingIds.has(t.id);

                  return (
                    <span
                      key={t.id}
                      className="colored-pill-text yellow d-inline-flex align-items-center m-1"
                      title={isDeleting ? "Deleting…" : "Existing term (saved)"}
                      style={isDeleting ? { opacity: 0.6 } : undefined} // optional fade
                    >
                      {t.term}
                      <button
                        type="button"
                        className="btn btn-sm btn-link ms-1 p-0"
                        onClick={() => {
                          if (!t.id || t.id.startsWith('tmp_')) {
                            setExisting(prev => prev.filter(x => x.id !== t.id));
                            return;
                          }
                          removeExisting(t.id);
                        }}
                        disabled={deletingIds.has(t.id)}
                        style={{ lineHeight: 1, color: "var(--font-color)" }}
                      >
                        {deletingIds.has(t.id) ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '0.9rem', height: '0.9rem' }} />
                        ) : (
                          <span aria-hidden>&times;</span>
                        )}
                      </button>

                    </span>
                  );
                })}
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 border rounded p-2">
              {filteredNewTerms.map((t) => (
                <span
                  key={t.id}
                  className="colored-pill-text blue d-inline-flex align-items-center m-1"
                  title="New term (not saved yet)"
                >
                  {t.term}
                  <button
                    type="button"
                    className="btn btn-sm btn-link ms-1 p-0"
                    aria-label={`Remove ${t.term}`}
                    onClick={() =>
                      onChange(filteredNewTerms.filter((x) => x.id !== t.id))
                    }
                    style={{ lineHeight: 1, color: "var(--font-color)" }}
                  >
                    &times;
                  </button>
                </span>
              ))}

              <input
                ref={inputRef}
                type="text"
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                onPaste={onPaste}
                className="flex-grow-1 border-0 outline-0"
                placeholder={placeholder}
                style={{ minWidth: 160 }}
              />
            </div>

            {error?.message === "required" && (
              <span className="form-text text-danger">required</span>
            )}
            <small className="form-text text-muted">
              To add new terms, press{" "}
              <span className="ghost-pill-text">Enter</span>,{" "}
              <span className="ghost-pill-text">Tab</span> or{" "}
              <span className="ghost-pill-text">,</span>. Min {minTermLen} chars.
              Duplicates (including existing) are ignored.
            </small>
          </div>
        );
      }}
    />
  );
};
